import { useCallback, useMemo } from "preact/hooks";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router";
import { chatbots } from "@/data/chatbots.js";
import { graphics } from "@/data/graphics.js";
import { information } from "@/data/information.js";
import { overlays } from "@/data/overlays.jsx";
import "./global-sidebar-menu.css";
import { dashboards } from "@/data/dashboards.jsx";

GlobalSidebarMenu.propTypes = {
  searchValue: PropTypes.string,
  onLinkClick: PropTypes.func,
};

const appEnv = import.meta.env.VITE_APP_ENV || "dev";

const overrideShow = appEnv === "dev" || appEnv === "qa";

export default function GlobalSidebarMenu({ searchValue = "", onLinkClick = () => {} }) {
  const location = useLocation();
  const currentPage = useMemo(() => location.pathname, [location]);

  const sortItems = useCallback((a, b) => {
    if (Object.hasOwn(a, "order") && Object.hasOwn(b, "order")) {
      return a.order - b.order;
    }
    return a.name.localeCompare(b.name);
  }, []);

  const allOverlays = useMemo(() => {
    return Object.values(overlays)
      .filter((overlay) => overlay.category !== "progress")
      .filter((overlay) => overlay.show || overrideShow)
      .filter((overlay) => {
        if (searchValue.length > 0) {
          return overlay.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const progressBars = useMemo(() => {
    return Object.values(overlays)
      .filter((overlay) => overlay.category === "progress")
      .filter((overlay) => overlay.show || overrideShow)
      .filter((overlay) => {
        if (searchValue.length > 0) {
          return overlay.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const dashboardItems = useMemo(() => {
    return Object.values(dashboards)
      .filter((dashboard) => dashboard.show || overrideShow)
      .filter((dashboard) => {
        if (searchValue.length > 0) {
          return dashboard.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const graphicsBundles = useMemo(() => {
    return Object.values(graphics)
      .filter((bundle) => bundle.show || overrideShow)
      .filter((bundle) => {
        if (searchValue.length > 0) {
          return bundle.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const informationItems = useMemo(() => {
    return Object.values(information)
      .filter((item) => item.show || overrideShow)
      .filter((item) => {
        if (searchValue.length > 0) {
          return item.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const chatbotItems = useMemo(() => {
    return Object.values(chatbots)
      .filter((chatbot) => chatbot.show || overrideShow)
      .filter((chatbot) => {
        if (searchValue.length > 0) {
          return chatbot.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const overlaySections = useMemo(() => {
    return [
      {
        name: "Progress Bars",
        children: progressBars.map((progressBar) => {
          return {
            name: progressBar.name.replace("Progress Bar", ""),
            link: progressBar.link,
            updated: progressBar.isUpdated,
            new: progressBar.isNew,
            show: progressBar.show,
          };
        }),
      },
      {
        name: "Fundraising Tools",
        children: allOverlays.map((overlay) => {
          return {
            name: overlay.name,
            link: overlay.link,
            updated: overlay.isUpdated,
            new: overlay.isNew,
            show: overlay.show,
          };
        }),
      },
    ];
  }, [progressBars, allOverlays]);

  const dashboardSection = useMemo(
    () =>
      dashboardItems.map((dashboard) => {
        return {
          name: dashboard.name,
          link: dashboard.link,
          updated: dashboard.isUpdated,
          new: dashboard.isNew,
          featured: dashboard.isFeatured,
          show: dashboard.show,
        };
      }),
    [dashboardItems],
  );

  const graphicsBundleSections = useMemo(
    () =>
      graphicsBundles.map((bundle) => {
        return {
          name: bundle.name,
          link: bundle.link,
          updated: bundle.isUpdated,
          new: bundle.isNew,
          featured: bundle.isFeatured,
          show: bundle.show,
        };
      }),
    [graphicsBundles],
  );

  const informationSection = useMemo(
    () =>
      informationItems.map((item) => {
        return {
          name: item.name,
          link: item.link,
          updated: item.isUpdated,
          new: item.isNew,
          featured: item.isFeatured,
          show: item.show,
        };
      }),
    [informationItems],
  );

  const chatbotSection = useMemo(
    () =>
      chatbotItems.map((item) => {
        return {
          name: item.name.replace(" Chatbot", ""),
          link: item.link,
          updated: item.isUpdated,
          new: item.isNew,
          featured: item.isFeatured,
          show: item.show,
        };
      }),
    [chatbotItems],
  );

  const formatName = useCallback((name) => {
    if (name === "Leaderboard Overlay") {
      return "Leaderboard";
    }

    return name;
  }, []);

  const makeMenuItem = useCallback(
    (item) => {
      const innerItem = (
        <>
          {formatName(item.name)}
          {item.new && (
            <span className="sj-light-blue border-none text-white badge badge-xs w-[fit-content] font-bold">
              <span className="inline-block">NEW</span>
            </span>
          )}
          {item.updated && (
            <span className="sj-dark-yellow border-none text-black badge badge-xs w-[fit-content] font-bold">
              <span className="inline-block ">UPDATED</span>
            </span>
          )}
          {item.featured && (
            <span className="sj-primary border-none badge badge-xs w-[fit-content] font-bold">
              <span className="inline-block">FEATURED</span>
            </span>
          )}
        </>
      );

      return (
        <>
          <li className="hidden py-1 relative lg:flex! h-[36px]" key={item.link}>
            <NavLink
              unstable_viewTransition
              to={item.link}
              className={
                item.className
                  ? `flex flex-row justify-between leading-[1.6]  ${item.className} ${currentPage === item.link && "menu-active"}`
                  : `flex flex-row justify-between leading-[0.8] ${!item.show && overrideShow && "text-red-600"} ${currentPage === item.link && "menu-active"}`
              }
            >
              {innerItem}
            </NavLink>
          </li>
          <li className="py-1 relative flex lg:hidden  h-[36px]" key={`${item.link}-mobile`}>
            <NavLink
              unstable_viewTransition
              to={item.link}
              onClick={() => onLinkClick()}
              className={
                item.className
                  ? `flex flex-row justify-between leading-[1.6] ${item.className}  ${currentPage === item.link && "menu-active"}`
                  : `flex flex-row justify-between leading-[0.8] ${!item.show && overrideShow && "text-red-600"}  ${currentPage === item.link && "menu-active"}`
              }
            >
              {innerItem}
            </NavLink>
          </li>
        </>
      );
    },
    [formatName, onLinkClick, currentPage],
  );

  const makeMenuSection = useCallback(
    (section) => {
      return (
        <li key={section.name}>
          <h2 className="menu-title">{section.name}</h2>
          <ul>{section.children.map((child) => makeMenuItem(child))}</ul>
        </li>
      );
    },
    [makeMenuItem],
  );

  const makeMenuItems = useCallback(
    (item) => {
      if (Object.hasOwn(item, "children")) {
        return makeMenuSection(item);
      }
      return makeMenuItem(item);
    },
    [makeMenuItem, makeMenuSection],
  );

  const mainSection = useMemo(() => {
    const otherSections = [
      ...overlaySections,
      {
        name: "Dashboards",
        children: dashboardSection,
      },
      {
        name: "Chatbots",
        children: chatbotSection,
      },
      {
        name: "Graphics and Assets",
        children: graphicsBundleSections,
      },
      {
        name: "Information",
        children: informationSection,
      },
    ].filter((item) => item.children.length > 0);

    const nowMonth = new Date().getMonth();

    return [
      {
        name: "Home",
        link: "/",
        className: "font-bold text-lg py-1",
      },
      nowMonth === 9
        ? {
            name: "CCAM Tools",
            link: "/ccam",
            className: " py-1 font-bold",
          }
        : null,
      ...otherSections,
    ].filter(Boolean);
  }, [
    overlaySections,
    chatbotSection,
    graphicsBundleSections,
    informationSection,
    dashboardSection,
  ]);

  return (
    <ul className="global-sidebar-menu menu text-base-content w-full md:w-80 pb-10">
      {mainSection.map((section) => makeMenuItems(section))}
    </ul>
  );
}
